import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Icon,
} from "semantic-ui-react";
import { useAuth } from "../App";
import { registerNotifications, unregisterNotifications } from "../utils/firebase";
import { getDatabase, ref, set, connectDatabaseEmulator } from "firebase/database";
import { trackEnableEmailNotificationsEvent, trackEnablePushNotificationsEvent } from "../utils/analytics";
import ChangePasswordDialog from "../components/dialog/ChangePasswordDialog";
import { useToast } from "../components/toast/ToastContext";

export default function SettingsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  useEffect(() => {
    if (
      !("Notification" in window) ||
      !("serviceWorker" in navigator || localStorage.getItem("fcmToken"))
    ) {
      setNotificationsEnabled(false);
      return;
    }

    const checkNotifications = async () => {
      if (Notification.permission !== "granted") {
        setNotificationsEnabled(false);
        return;
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setNotificationsEnabled(!!subscription);
    };
    checkNotifications();
  }, []);

  const handleEnableNotification = () => {
    if (user.email) {
      registerNotifications(user.uid, () => {
        toast.success("Push notifikácie zapnuté.")
        setNotificationsEnabled(true);
      });
      trackEnablePushNotificationsEvent();
    }
  };

  const handleDisableNotification = () => {
    if (user.email) {
      unregisterNotifications(user.uid, () => {
        toast.success("Push notifikácie vypnuté.")
        setNotificationsEnabled(false);
      });
    }
  };

  const toggleEmailNotification = (value) => {
    if (!user?.uid) return;
    const db = getDatabase();
    if (process.env.REACT_APP_USE_EMULATORS === "true") {
      console.log("CONNECTING TO EMULATORS...");
      connectDatabaseEmulator(db, "localhost", 9000);
    }

    const userRef = ref(db, `/users/${user.uid}/emailNotification`);
    set(userRef, value)
      .then(() => {
        if (value) {
          trackEnableEmailNotificationsEvent();
        }
        toast.success(value ? "Emailové notifikácie zapnuté" : "Emailové notifikácie vypnuté");
      })
      .catch((err) => {
        console.error("Chyba pri ukladaní emailNotification:", err);
        toast.success("Nepodarilo sa zmeniť nastavenie emailových notifikácií");
      });
  };

  if (!user || !user.email || !user.data) {
    return <div className="App">Načítavam...</div>;
  }

  const handleChangePasswordButton = () => {
    setChangePasswordOpen(true);
  }

  const hasOrders = () => !!user.data.orders && Object.values(user.data.orders).length > 0;

  return (
    <div className="App" style={{ padding: "2rem", height: '80vh', overflow: 'auto' }}>
      <Card.Group stackable itemsPerRow={3} doubling>
        {/* === ALIAS === */}
        <Card>
          <Card.Content
            textAlign="center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Card.Header style={{ marginBottom: "1rem" }}>Alias</Card.Header>
            <div>
              <Icon name="qrcode" size="huge" color="black" />
            </div>
            <Card.Description style={{ marginTop: "1rem", color: "black" }}>
              {user.data.alias}
            </Card.Description>
          </Card.Content>
          <Card.Content extra textAlign="center">
          </Card.Content>
        </Card>

        {/* === BALÍK === */}
        <Card>
          <Card.Content
            textAlign="center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Card.Header style={{ marginBottom: "1rem" }}>Aktuálna verzia</Card.Header>
            <div>
              <Icon name="box" size="huge" color="black" />
            </div>
            <Card.Description style={{ marginTop: "1rem", color: "black" }}>
              {user.data.plan}
            </Card.Description>
          </Card.Content>
          <Card.Content extra textAlign="center">
            <Button color="yellow" size="tiny" disabled>
              <Icon name="refresh" /> ZMENIŤ VERZIU
            </Button>
          </Card.Content>
        </Card>

        {/* === OBJEDNÁVKY === */}
        <Card>
          <Card.Content
            textAlign="center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Card.Header style={{ marginBottom: "1rem" }}>Objednávky</Card.Header>
            <div>
              <Icon name="shopping bag" size="huge" color="black" />
            </div>
            <Card.Description style={{ color: "black", marginTop: "1rem" }}>
              {hasOrders() ? "Áno" : "Žiadne"}
            </Card.Description>
          </Card.Content>
          <Card.Content extra textAlign="center">
            {/* Rezervovaný priestor */}
          </Card.Content>
        </Card>

        {/* === EMAIL === */}
        <Card>
          <Card.Content
            textAlign="center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Card.Header style={{ marginBottom: "1rem" }}>Email</Card.Header>
            <div>
              <Icon name="mail" size="huge" color="black" />
            </div>
            <Card.Description style={{ marginTop: "1rem", color: "black" }}>
              {user.email}
            </Card.Description>
          </Card.Content>
          <Card.Content extra textAlign="center">
            <Button
              color="yellow"
              size="tiny"
              onClick={handleChangePasswordButton}
            >
              <Icon name="lock" /> ZMENIŤ HESLO
            </Button>
          </Card.Content>
        </Card>

        {/* === EMAIL NOTIFIKÁCIE === */}
        <Card>
          <Card.Content
            textAlign="center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Card.Header style={{ marginBottom: "1rem" }}>
              Emailové notifikácie
            </Card.Header>
            <div>
              <Icon name="envelope open outline" size="huge" color="black" />
            </div>
            <Card.Description style={{ marginTop: "1rem", color: "black" }}>
              {user.data.emailNotification ? "Aktívne" : "Neaktívne"}
            </Card.Description>
          </Card.Content>
          <Card.Content extra textAlign="center">
            <Button
              color="yellow"
              size="tiny"
              onClick={() =>
                toggleEmailNotification(!user.data.emailNotification)
              }
            >
              {user.data.emailNotification
                ? "DEAKTIVOVAŤ"
                : "AKTIVOVAŤ"}
            </Button>
          </Card.Content>
        </Card>

        {/* === ZARIADENIE NOTIFIKÁCIE === */}
        <Card>
          <Card.Content
            textAlign="center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Card.Header style={{ marginBottom: "1rem" }}>
              Notifikácie na zariadení
            </Card.Header>
            <div>
              <Icon name="bell" size="huge" color="black" />
            </div>
            <Card.Description style={{ color: "black", marginTop: "1rem" }}>
              {notificationsEnabled ? "Áno" : "Nie"}
            </Card.Description>
          </Card.Content>
          <Card.Content extra textAlign="center">
            <Button
              color="yellow"
              size="tiny"
              onClick={
                notificationsEnabled
                  ? handleDisableNotification
                  : handleEnableNotification
              }
            >
              {notificationsEnabled ? "DEAKTIVOVAŤ" : "AKTIVOVAŤ"}
            </Button>
          </Card.Content>
        </Card>
      </Card.Group>
      <ChangePasswordDialog open={changePasswordOpen} onClose={() => { setChangePasswordOpen(false) }}></ChangePasswordDialog>
    </div>
  );

}
